import { useState } from "react";
import type { AppSettings } from "@/lib/queries";
import { useUpdateAppSettings } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, HardDrive } from "lucide-react";
import { toast } from "sonner";

/**
 * Cloudflare R2 storage config card (Settings → Integrations).
 *
 * The API presign/download path (routes/documents.ts getR2Creds) reads these
 * values from app_settings — access key / secret key / API token are stored
 * AES-GCM-encrypted (SETTINGS_ENC_KEY) and masked as "***" in GET responses.
 * PATCH treats "***" as "keep existing" (appSettings.ts r2SecretKeys handling).
 */
export function R2Card({ settings, canEdit }: { settings: AppSettings; canEdit: boolean }) {
  const [form, setForm] = useState({
    r2Enabled: settings.r2Enabled,
    r2AccountId: settings.r2AccountId ?? "",
    r2BucketName: settings.r2BucketName ?? "",
    r2AccessKey: settings.r2AccessKey === "***" ? "" : settings.r2AccessKey ?? "",
    r2SecretKey: settings.r2SecretKey === "***" ? "" : settings.r2SecretKey ?? "",
    r2S3Endpoint: settings.r2S3Endpoint ?? "",
  });
  const update = useUpdateAppSettings();

  const save = async () => {
    // Partial patch — only changed fields. Secret fields follow the "***" keep
    // convention: blank + already set → "***" (keep); typed → new value.
    const patch: Record<string, unknown> = {};
    if (form.r2Enabled !== settings.r2Enabled) patch.r2Enabled = form.r2Enabled;
    if (form.r2AccountId !== (settings.r2AccountId ?? "")) patch.r2AccountId = form.r2AccountId || null;
    if (form.r2BucketName !== (settings.r2BucketName ?? "")) patch.r2BucketName = form.r2BucketName || null;
    if (form.r2S3Endpoint !== (settings.r2S3Endpoint ?? "")) patch.r2S3Endpoint = form.r2S3Endpoint || null;
    if (form.r2AccessKey !== "") patch.r2AccessKey = form.r2AccessKey;
    else if (settings.r2AccessKey) patch.r2AccessKey = "***";
    if (form.r2SecretKey !== "") patch.r2SecretKey = form.r2SecretKey;
    else if (settings.r2SecretKey) patch.r2SecretKey = "***";

    if (Object.keys(patch).length === 0) {
      toast.message("No changes to save");
      return;
    }
    try {
      await update.mutateAsync(patch);
      toast.success("R2 settings saved");
    } catch { /* handled by mutation onError */ }
  };

  const isConfigured = !!(settings.r2AccountId && settings.r2BucketName && settings.r2AccessKey && settings.r2SecretKey);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500 flex items-center gap-2"><HardDrive size={16} /> Cloudflare R2 Storage</CardTitle>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted">Enabled</Label>
          <Switch checked={form.r2Enabled} onCheckedChange={(v) => setForm({ ...form, r2Enabled: v })} disabled={!canEdit} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isConfigured && (
          <Alert>
            <AlertDescription>
              R2 is not fully configured — document upload/download will return <code>r2_not_configured</code> until account ID, bucket name, access key and secret key are set.
            </AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label>Account ID</Label>
            <Input value={form.r2AccountId} disabled={!canEdit} onChange={(e) => setForm({ ...form, r2AccountId: e.target.value })} placeholder="32-hex Cloudflare account ID" />
          </div>
          <div>
            <Label>Bucket Name</Label>
            <Input value={form.r2BucketName} disabled={!canEdit} onChange={(e) => setForm({ ...form, r2BucketName: e.target.value })} placeholder="emr-documents" />
          </div>
          <div>
            <Label>Access Key ID {settings.r2AccessKey === "***" && <span className="text-xs text-muted ml-2">(currently set — leave blank to keep)</span>}</Label>
            <Input type="password" value={form.r2AccessKey} disabled={!canEdit} onChange={(e) => setForm({ ...form, r2AccessKey: e.target.value })} placeholder={settings.r2AccessKey === "***" ? "••••••••" : "R2 S3 access key ID"} />
          </div>
          <div>
            <Label>Secret Access Key {settings.r2SecretKey === "***" && <span className="text-xs text-muted ml-2">(currently set — leave blank to keep)</span>}</Label>
            <Input type="password" value={form.r2SecretKey} disabled={!canEdit} onChange={(e) => setForm({ ...form, r2SecretKey: e.target.value })} placeholder={settings.r2SecretKey === "***" ? "••••••••" : "R2 S3 secret access key"} />
          </div>
          <div className="md:col-span-2">
            <Label>S3 Endpoint <span className="text-xs text-muted ml-1">(optional — defaults to https://&lt;accountId&gt;.r2.cloudflarestorage.com)</span></Label>
            <Input value={form.r2S3Endpoint} disabled={!canEdit} onChange={(e) => setForm({ ...form, r2S3Endpoint: e.target.value })} placeholder="https://<account>.r2.cloudflarestorage.com" />
          </div>
        </div>
        <p className="text-xs text-muted">
          Documents (patient uploads and generated prescriptions/referrals/certificates) are stored in this R2 bucket. Create the bucket and API token in the Cloudflare dashboard (R2 → Manage API Tokens), then paste the credentials here.
        </p>
        {canEdit && (
          <Button onClick={save} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Save size={14} className="mr-1" /> Save R2 settings
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
